import './OuputSettings.scss';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ISelectItem} from '../../../../shared/types';
import {
  outputSettingsSchema,
  OutputSettingsFormData,
} from '../../../../schemas/productions/reProductionSchema';
import {
  ToastType,
  FormMode,
  ModuleType,
  FormAction,
  ProductionStatus,
} from '../ReProductionUtils';
import {
  useGetProductLocationView,
  useValidateDestination,
  useCreateDestination,
  useSaveProductionLocation,
} from '../../../../hooks/productions/useProductions';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice';
import {
  useGetReProductLocationView,
  useSaveReProductionLocation,
} from '../../../../hooks/productions/useReProductions';

export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface outputSettingsProps {
  productionId: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  mode?: FormMode;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  skipToDocumentSelection?: () => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

type SubmissionContext = {
  formData: OutputSettingsFormData;
  markComplete: boolean;
  rootPathId: number;
  rootPathName: string;
};

type FolderModalState = {
  show: boolean;
  message: string;
  context: SubmissionContext | null;
};

const OutputSettingsInner: ForwardRefRenderFunction<
  StepChildRef,
  outputSettingsProps
> = (
  {
    productionId,
    isProductionSet,
    getStatus,
    goNext,
    mode = FormMode.CREATE,
    onStateChange,
    productionStatusCode = null,
    isUpdateNeeded = true,
    onUpdateNeededChange,
  },
  ref
) => {
  const defaultValues: OutputSettingsFormData = {
    action: FormAction.SAVE,
    rootPath: '',
    directory: '',
    archiveProduction: false,
    loadFilePathEnabled: false,
    filePath: 'Z:\\',
    volumeIncluded: false,
    volumeName: 'VOL00001',
    volumeLocation: '',
    nativeFolder: '',
    pdfFolder: '',
    textFolder: '',
    mp3Folder: '',
    imagesFolder: '',
    loadFilesFolder: '',
    translationsFolder: '',
    splitSubFolders: false,
    splitCount: '0',
  };

  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: {errors, isDirty},
    watch,
  } = useForm<OutputSettingsFormData>({
    mode: 'onSubmit',
    resolver: zodResolver(outputSettingsSchema()),
    defaultValues,
  });

  const [rootPathOptions, setRootPathOptions] = useState<ISelectItem[]>([]);
  const rootPathMapRef = useRef<Record<string, string>>({});
  const [volumeLocationOptions, setVolumeLocationOptions] = useState<
    ISelectItem[]
  >([]);
  const [folderModalState, setFolderModalState] = useState<FolderModalState>({
    show: false,
    message: '',
    context: null,
  });
  const [showArchiveWarning, setShowArchiveWarning] = useState(false);
  const [showRootPathError, setShowRootPathError] = useState(false);
  const [pendingSubmission, setPendingSubmission] =
    useState<SubmissionContext | null>(null);

  const loadFilePathEnabled = watch('loadFilePathEnabled');
  const volumeIncluded = watch('volumeIncluded');
  const splitSubFolders = watch('splitSubFolders');
  const rootPathValue = watch('rootPath');
  const volumeLocationValue = watch('volumeLocation');

  const isInProgressOrCompleted =
    productionStatusCode === ProductionStatus.COMPLETED ||
    productionStatusCode == ProductionStatus.INPROGRESS;
  const modeVal = isInProgressOrCompleted ? FormMode.VIEW : mode;
  const isFieldDisabled = modeVal === FormMode.VIEW;
  const isReproductionFieldDisabled = true;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isDirty) {
      dispatch(setReproductionIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [isDirty]);

  const rootPathDropdownLabel = t('reProduction.outputSettings.rootPathLabel', {
    defaultValue: t('reProduction.outputSettings.rootPath'),
  });
  const volumeLocationDropdownLabel = t(
    'reProduction.outputSettings.volumeLocationLabel',
    {
      defaultValue: t('reProduction.outputSettings.volumeLocation'),
    }
  );
  const filePathLabel = t('reProduction.outputSettings.filePathLabel', {
    defaultValue: t('reProduction.outputSettings.filePath'),
  });
  const splitCountLabel = t('reProduction.outputSettings.splitCountLabel', {
    defaultValue: t('reProduction.outputSettings.splitCount'),
  });
  const createFolderTitle = t('reProduction.outputSettings.createFolderTitle');
  const createFolderDescription = t(
    'reProduction.outputSettings.createFolderDescription'
  );
  const createFolderConfirm = t(
    'reProduction.outputSettings.createFolderConfirm',
    {defaultValue: 'Yes'}
  );
  const createFolderCancel = t(
    'reProduction.outputSettings.createFolderCancel',
    {defaultValue: 'No'}
  );

  useEffect(() => {
    register('rootPath');
  }, [register]);

  const {data: reProductLocationData, isLoading: isProductLocationLoading} =
    useGetReProductLocationView(
      {
        SecurityGroupId: selectedSecurityGroupId ?? 0,
        reProductionId: productionId,
        moduleType: ModuleType.REPRODUCTION,
      },
      Boolean(selectedSecurityGroupId && productionId)
    );
  const validateDestinationMutation = useValidateDestination();
  const createDestinationMutation = useCreateDestination();
  const saveReProductionLocationMutation = useSaveReProductionLocation();

  useEffect(() => {
    if (!reProductLocationData) return;

    const rootPaths = reProductLocationData.lstReProductionRootPath ?? [];
    const availableRootPaths: ISelectItem[] = rootPaths.map((item) => ({
      id: String(item.reProductionRootId),
      name: item.reProductionRootLabel ?? '',
    }));

    const rootMap: Record<string, string> = {};
    availableRootPaths.forEach((option: ISelectItem) => {
      const idStr = String(option.id ?? '');
      if (idStr && idStr !== '') {
        rootMap[idStr] = String(option.name ?? '');
      }
    });
    rootPathMapRef.current = rootMap;

    const rootOptionsWithPlaceholder: ISelectItem[] = [
      {
        id: '',
        name: t('reProduction.outputSettings.rootPathPlaceholder'),
      },
      ...availableRootPaths,
    ];
    setRootPathOptions(rootOptionsWithPlaceholder);

    const volumeEntries = reProductLocationData.volumenLocation
      ? (Object.entries(reProductLocationData.volumenLocation) as [
          string,
          string | null,
        ][])
      : [];
    const volumeOptions: ISelectItem[] = volumeEntries.map(([key, value]) => ({
      id: key,
      name: value ?? key,
    }));
    setVolumeLocationOptions(volumeOptions);

    const output = reProductLocationData.reProductionOutputLocation;
    const folders = reProductLocationData.reProductionComponentsFolderDetails;

    const resolvedRootPathId =
      output?.reProductionRootPathID !== undefined &&
      output?.reProductionRootPathID !== null
        ? String(output.reProductionRootPathID)
        : '';

    const defaultVolumeLocationId =
      volumeOptions.length > 0 &&
      volumeOptions[0].id !== undefined &&
      volumeOptions[0].id !== null
        ? String(volumeOptions[0].id)
        : '';
    const resolvedVolumeLocation =
      output?.isVolumeIncluded && output?.volumeLocationID
        ? String(output.volumeLocationID)
        : defaultVolumeLocationId;

    // Ensure default values are set if backend doesn't provide them
    // Always set defaults so they're available when toggles are turned on
    reset({
      action: FormAction.SAVE,
      rootPath: resolvedRootPathId,
      directory: output?.reProductionDirectory ?? '',
      archiveProduction: output?.shouldArchive ?? false,
      loadFilePathEnabled: output?.shouldIncludeDrive ?? false,
      filePath: output?.driveText || 'Z:\\',
      volumeIncluded: output?.isVolumeIncluded ?? false,
      volumeName: output?.volumeName || 'VOL00001',
      volumeLocation: resolvedVolumeLocation,
      nativeFolder: folders?.folderName_Natives ?? '',
      pdfFolder: folders?.folderName_PDF ?? '',
      textFolder: folders?.folderName_Text ?? '',
      mp3Folder: folders?.folderName_MP3 ?? '',
      imagesFolder: folders?.folderName_Images ?? '',
      loadFilesFolder: folders?.folderName_LoadFiles ?? '',
      translationsFolder: folders?.folderName_Translations ?? '',
      splitSubFolders: folders?.toSplitSubFolders ?? false,
      splitCount: String(folders?.splitCount ?? 0),
    });
  }, [reProductLocationData, reset, t]);

  const selectedRootPath =
    rootPathOptions.find((option) => option.id === rootPathValue) ??
    rootPathOptions.find((option) => option.id === '') ??
    null;
  const selectedVolumeLocation =
    volumeLocationOptions.find((option) => option.id === volumeLocationValue) ??
    null;

  const isMutating =
    validateDestinationMutation.isPending ||
    createDestinationMutation.isPending ||
    saveReProductionLocationMutation.isPending;

  const isSaveDisabled = modeVal === FormMode.VIEW || isMutating;
  const isNextDisabled =
    (mode === FormMode.VIEW && !isInProgressOrCompleted) || isMutating;

  useEffect(() => {
    onStateChange?.({
      saveDisabled: isSaveDisabled,
      nextDisabled: isNextDisabled,
    });
  }, [isSaveDisabled, isNextDisabled, onStateChange]);

  // Set default volume location to first option if empty and options are available
  useEffect(() => {
    if (
      volumeLocationOptions.length > 0 &&
      (!volumeLocationValue || volumeLocationValue.trim() === '')
    ) {
      const firstOptionId = volumeLocationOptions[0]?.id;
      if (firstOptionId) {
        setValue('volumeLocation', String(firstOptionId), {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [volumeLocationOptions, volumeLocationValue, setValue]);

  const handleToggleChange =
    (
      field: keyof OutputSettingsFormData,
      resetFields?: Array<keyof OutputSettingsFormData>
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setValue(field, checked, {shouldDirty: true});
      resetFields?.forEach((resetField) => {
        if (!checked) {
          // Don't clear the value, just clear errors
          clearErrors(resetField);
        } else {
          // When toggling back on, restore default values if field is empty
          const currentValue = watch(resetField);
          if (!currentValue || currentValue === '') {
            if (resetField === 'filePath') {
              setValue(resetField, 'Z:\\', {
                shouldDirty: true,
                shouldValidate: false,
              });
            } else if (resetField === 'volumeName') {
              setValue(resetField, 'VOL00001', {
                shouldDirty: true,
                shouldValidate: false,
              });
            } else if (resetField === 'splitCount') {
              setValue(resetField, '1000', {
                shouldDirty: true,
                shouldValidate: false,
              });
            }
          }
        }
      });
    };

  const buildSubmissionContext = (
    formData: OutputSettingsFormData
  ): SubmissionContext | null => {
    const directory = formData.directory?.trim() ?? '';
    if (!directory) {
      setError('directory', {
        type: 'manual',
        message: t('reProduction.outputSettings.errors.directoryRequired'),
      });
      return null;
    }

    if (!formData.rootPath || !formData.rootPath.trim()) {
      setShowRootPathError(true);
      return null;
    }

    let rootPathName = rootPathMapRef.current[formData.rootPath] || '';
    if (
      !rootPathName &&
      reProductLocationData?.reProductionOutputLocation
        ?.reProductionRootPathName
    ) {
      rootPathName =
        reProductLocationData.reProductionOutputLocation
          .reProductionRootPathName ?? '';
    }

    if (!rootPathName) {
      setShowRootPathError(true);
      return null;
    }

    return {
      formData: {
        ...formData,
        directory,
        filePath: formData.filePath?.trim() ?? '',
      },
      markComplete: formData.action === FormAction.NEXT,
      rootPathId: Number(formData.rootPath),
      rootPathName,
    };
  };

  const executeSave = async (context: SubmissionContext) => {
    try {
      if (!isUpdateNeeded) {
        goNext?.();
        return;
      }
      const parsedSplitCount = Number(context.formData.splitCount);
      const splitCountValue = Number.isNaN(parsedSplitCount)
        ? 0
        : parsedSplitCount;
      const shouldIncludeDrive = context.formData.loadFilePathEnabled;
      const isVolumeIncluded = context.formData.volumeIncluded;
      const isSplitSubFolders = context.formData.splitSubFolders;
      const safeFolderValue = (value: string | undefined) => value ?? '';

      // Build ProductionComponentsFolderDetails conditionally
      const productionComponentsFolderDetails: any = {
        FolderName_Natives: safeFolderValue(context.formData.nativeFolder),
        FolderName_PDF: safeFolderValue(context.formData.pdfFolder),
        FolderName_Text: safeFolderValue(context.formData.textFolder),
        FolderName_MP3: safeFolderValue(context.formData.mp3Folder),
        FolderName_Images: safeFolderValue(context.formData.imagesFolder),
        FolderName_LoadFiles: safeFolderValue(context.formData.loadFilesFolder),
        FolderName_Translations: safeFolderValue(
          context.formData.translationsFolder
        ),
        ToSplitSubFolders: isSplitSubFolders,
      };

      // Only include SplitCount if splitSubFolders is true
      if (isSplitSubFolders) {
        productionComponentsFolderDetails.SplitCount = splitCountValue;
      }

      const payload = {
        ReProductionId: productionId,
        IsMarkComplete: context.markComplete,
        ModuleType: ModuleType.REPRODUCTION,
        ReProductionOutputLocation: {
          FileServerID: context.rootPathId,
          ReProductionDirectory: context.formData.directory,
          ShouldArchive: context.formData.archiveProduction,
        },
        SecurityGroupId: selectedSecurityGroupId ?? 0,
      };

      await saveReProductionLocationMutation.mutateAsync(payload);

      getStatus(
        true,
        ToastType.Success,
        context.markComplete
          ? t('reProduction.toastMessages.nextSuccess')
          : t('reProduction.toastMessages.saveSuccess')
      );
      // Update parent state to track that update is needed after save (only for SAVE action)
      if (!context.markComplete) {
        onUpdateNeededChange?.(true);
      }
      if (context.markComplete) {
        goNext?.();
      }
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message || t('reProduction.outputSettings.saveError')
      );
    }
    dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
  };

  const createDestinationAndSave = async (context: SubmissionContext) => {
    try {
      // Call createDestination first - if 200 OK, proceed to save
      await createDestinationMutation.mutateAsync({
        RootPath: context.rootPathName,
        DestinationFolder: context.formData.directory,
      });

      // After createDestination succeeds (200 OK), proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t('reProduction.outputSettings.createDestinationError')
      );
    }
  };

  const proceedAfterValidation = async (context: SubmissionContext) => {
    if (!context.formData.archiveProduction && context.markComplete) {
      setPendingSubmission(context);
      setShowArchiveWarning(true);
      return;
    }

    setPendingSubmission(null);
    await executeSave(context);
  };

  const beginSubmission = async (formData: OutputSettingsFormData) => {
    const context = buildSubmissionContext(formData);
    if (!context) return;

    try {
      // Step 1: Validate destination
      const validationResult = await validateDestinationMutation.mutateAsync({
        rootPath: context.rootPathName,
      });

      if (!validationResult.success) {
        getStatus(
          true,
          ToastType.Error,
          validationResult.message ||
            t('reProduction.outputSettings.validateError')
        );
        return;
      }

      // Case 1: Root path doesn't exist (rootExists: false)
      // Response: {"success": true, "rootExists": false, "message": "The Production Root Path does not exist..."}
      const rootExists = (validationResult as any).rootExists;
      if (rootExists === false) {
        // Root path doesn't exist, show folder creation modal
        setFolderModalState({
          show: true,
          message: createFolderDescription,
          context,
        });
        return;
      }

      // Case 2: Root path exists (alreadyExists: true)
      // Response: {"success":true,"alreadyExists":true,"message":"Production root path exists."}
      // Flow: validateDestination → createDestination → saveProductionLocation
      if (validationResult.alreadyExists === true) {
        // Root path exists, call createDestination then saveProductionLocation
        await createDestinationAndSave(context);
        return;
      }

      // Case 3: alreadyExists is false (destination folder doesn't exist)
      if (validationResult.alreadyExists === false) {
        // Destination folder doesn't exist, show folder creation modal
        setFolderModalState({
          show: true,
          message: createFolderDescription,
          context,
        });
        return;
      }

      // Default: if validation passed, proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t('reProduction.outputSettings.validateError')
      );
    }
  };

  const handleArchiveWarningConfirm = async () => {
    if (!pendingSubmission) {
      setShowArchiveWarning(false);
      return;
    }

    const context = pendingSubmission;
    setShowArchiveWarning(false);
    setPendingSubmission(null);
    await executeSave(context);
  };

  const handleArchiveWarningCancel = () => {
    setPendingSubmission(null);
    setShowArchiveWarning(false);
  };

  const handleFolderModalCancel = () => {
    setFolderModalState({show: false, message: '', context: null});
  };

  const handleFolderModalConfirm = async () => {
    if (!folderModalState.context) {
      handleFolderModalCancel();
      return;
    }

    try {
      const context = folderModalState.context;
      // Call createDestination - if 200 OK, proceed to save
      await createDestinationMutation.mutateAsync({
        RootPath: context.rootPathName,
        DestinationFolder: context.formData.directory,
      });

      setFolderModalState({show: false, message: '', context: null});
      // After createDestination succeeds (200 OK), proceed to save
      await proceedAfterValidation(context);
    } catch (error) {
      getStatus(
        true,
        ToastType.Error,
        (error as Error)?.message ||
          t('reProduction.outputSettings.createDestinationError')
      );
      setFolderModalState({show: false, message: '', context: null});
    }
  };

  const onSubmit = async (data: OutputSettingsFormData) => {
    await beginSubmission(data);
  };

  useImperativeHandle(ref, () => ({
    save: () => {
      setValue('action', FormAction.SAVE);
      handleSubmit(onSubmit)();
    },
    next: () => {
      if (isInProgressOrCompleted) {
        goNext?.();
        return;
      }
      setValue('action', FormAction.NEXT);
      handleSubmit(onSubmit)();
    },
  }));

  if (isProductLocationLoading && !reProductLocationData) {
    return <ArrLoadingSpinner />;
  }

  return (
    <>
      <Box
        className="re-production-form-section"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        role="form"
        aria-label={t('reProduction.outputSettings.header')}
      >
        <Box className="re-production-form delivery-options">
          <Box className="re-production-form delivery-options__header">
            <Typography
              className="typo-text subhead2_size"
              data-testid="reproduction_steps_outputSettings_label"
            >
              {t('reProduction.steps.outputSettings')}
            </Typography>
          </Box>
          <Box className="info-box">
            <Box className="info-card">
              <InfoOutlinedIcon className="info-icon" />

              <Typography variant="body2" data-testid="reproduction_info_label">
                {t('reProduction.outputSettings.info-label')}
              </Typography>
            </Box>
          </Box>

          <Box className="re-production-form-body">
            <Box className="delivery-section">
              <Typography
                className="section-title typo-text subhead4_size"
                component="div"
                data-testid="reproduction_specifyLocation_label"
              >
                {t('reProduction.outputSettings.specifyLocation')}
              </Typography>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid="reproduction_rootPath_label"
                  >
                    {t('reProduction.outputSettings.rootPath')}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={rootPathDropdownLabel}
                    dropdownItems={rootPathOptions}
                    selectedValues={selectedRootPath}
                    onSelectionChange={(item: ISelectItem | null) => {
                      setValue('rootPath', String(item?.id ?? ''), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      clearErrors('rootPath');
                    }}
                    disabled={isFieldDisabled}
                    error={!!errors.rootPath}
                    sizeVariant="medium"
                    dataTestId="lstReProductionRootPaths"
                    aria-label={t('reProduction.outputSettings.rootPath')}
                  />
                </Box>
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label"
                    component="div"
                    data-testid="reproduction_directory_label"
                  >
                    {t('reProduction.outputSettings.directory')}
                  </Typography>
                  <ArrTextField
                    placeholder=""
                    {...register('directory', {
                      onChange: () => clearErrors('directory'),
                    })}
                    value={watch('directory')}
                    state={
                      isFieldDisabled
                        ? 'inactive'
                        : errors.directory
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.directory}
                    supportText={errors.directory?.message || ''}
                    disabled={isFieldDisabled}
                    sizeVariant="medium"
                    id="ReProduction_outputSettings_Directory"
                    aria-label={t('reProduction.outputSettings.directory')}
                    dataTestId="ReProduction_outputSettings_Directory"
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={watch('archiveProduction')}
                  onChange={handleToggleChange('archiveProduction')}
                  disabled={isFieldDisabled}
                  dataTestId="chkShouldArchive"
                />
                <Box className="toggle-label-wrapper">
                  <Typography
                    className="toggle-label typo-text body3_size"
                    component="div"
                    data-testid="reproduction_archiveProduction_label"
                  >
                    {t('reProduction.outputSettings.archiveProduction')}

                    <ArrTooltipWrapper
                      trigger={
                        <InfoOutlinedIcon className="delivery-info-icon" />
                      }
                    >
                      {t(
                        'reProduction.outputSettings.archiveProductionTooltip'
                      )}
                    </ArrTooltipWrapper>
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={loadFilePathEnabled}
                  onChange={handleToggleChange('loadFilePathEnabled', [
                    'filePath',
                  ])}
                  disabled={isReproductionFieldDisabled}
                  dataTestId="ProductionOutputLocation_ShouldIncludeDrive"
                />
                <Typography
                  className="toggle-label typo-text body3_size re-production-label-disabled"
                  component="div"
                  data-testid="reproduction_loadFilePath_label"
                >
                  {t('reProduction.outputSettings.loadFilePath')}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label re-production-label-disabled"
                    component="div"
                    data-testid="reproduction_filepath_label"
                  >
                    {filePathLabel}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'reProduction.outputSettings.filePathPlaceholder'
                    )}
                    {...register('filePath', {
                      onChange: () => clearErrors('filePath'),
                    })}
                    value={watch('filePath') || ''}
                    id="ReProduction_outputSettings_FilePath"
                    disabled={isReproductionFieldDisabled}
                    state={
                      isReproductionFieldDisabled
                        ? 'inactive'
                        : errors.filePath
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.filePath}
                    supportText={errors.filePath?.message || ''}
                    sizeVariant="medium"
                    aria-label={filePathLabel}
                    dataTestId="ReProduction_outputSettings_FilePath"
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={volumeIncluded}
                  onChange={handleToggleChange('volumeIncluded', [
                    'volumeName',
                    'volumeLocation',
                  ])}
                  disabled={isReproductionFieldDisabled}
                  dataTestId="ProductionOutputLocation_IsVolumeIncluded"
                />
                <Typography
                  className="toggle-label typo-text body3_size re-production-label-disabled"
                  component="div"
                  data-testid="reproduction_volumeIncluded_label"
                >
                  {t('reProduction.outputSettings.volumeIncluded')}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label re-production-label-disabled"
                    component="div"
                    data-testid="reproduction_volumeName_label"
                  >
                    {t('reProduction.outputSettings.volumeName')}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'reProduction.outputSettings.volumeNamePlaceholder'
                    )}
                    {...register('volumeName')}
                    value={watch('volumeName') || ''}
                    disabled={isReproductionFieldDisabled}
                    state={
                      isReproductionFieldDisabled
                        ? 'inactive'
                        : errors.volumeName
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.volumeName}
                    supportText={errors.volumeName?.message || ''}
                    sizeVariant="medium"
                    id="ReProduction_outputSettings_VolumeName"
                    aria-label={t('reProduction.outputSettings.volumeName')}
                    dataTestId="ReProduction_outputSettings_VolumeName"
                  />
                </Box>
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label re-production-label-disabled"
                    component="div"
                    data-testid="reproduction_volumeLocation_label"
                  >
                    {t('reProduction.outputSettings.volumeLocation')}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={volumeLocationDropdownLabel}
                    dropdownItems={volumeLocationOptions}
                    selectedValues={selectedVolumeLocation}
                    onSelectionChange={(item: ISelectItem | null) => {
                      setValue('volumeLocation', String(item?.id ?? ''), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    disabled={isReproductionFieldDisabled}
                    error={!!errors.volumeLocation}
                    errorText={errors.volumeLocation?.message || ''}
                    sizeVariant="medium"
                    dataTestId="ReProduction_outputSettings_VolumeLocation"
                    aria-label={t('reProduction.outputSettings.volumeLocation')}
                  />
                </Box>
              </Box>
            </Box>

            <Box className="delivery-section">
              <Typography
                className="section-title typo-text subhead4_size re-production-label-disabled"
                component="div"
                data-testid="reproduction_componentFolders_label"
              >
                {t('reProduction.outputSettings.componentFolders')}
              </Typography>
              <Box className="folder-grid">
                {(
                  [
                    {
                      field: 'nativeFolder',
                      label: 'nativeFolder',
                      apiField: 'Natives',
                    },
                    {
                      field: 'imagesFolder',
                      label: 'imagesFolder',
                      apiField: 'Images',
                    },
                    {field: 'pdfFolder', label: 'pdfFolder', apiField: 'PDF'},
                    {
                      field: 'loadFilesFolder',
                      label: 'loadFilesFolder',
                      apiField: 'LoadFiles',
                    },
                    {
                      field: 'textFolder',
                      label: 'textFolder',
                      apiField: 'Text',
                    },
                    {
                      field: 'translationsFolder',
                      label: 'translationsFolder',
                      apiField: 'Translations',
                    },
                    {field: 'mp3Folder', label: 'mp3Folder', apiField: 'MP3'},
                  ] as Array<{
                    field: keyof OutputSettingsFormData;
                    label: string;
                    apiField: string;
                  }>
                ).map(({field, label, apiField}) => (
                  <Box className="field-item" key={field as string}>
                    <Typography
                      className="typo-text body3_size label re-production-label-disabled"
                      component="div"
                      data-testid={`reproduction_${label}_label`}
                    >
                      {t(`reProduction.outputSettings.${label}`)}
                    </Typography>
                    <ArrTextField
                      {...register(field as keyof OutputSettingsFormData)}
                      value={
                        watch(field as keyof OutputSettingsFormData) as string
                      }
                      placeholder={t(`reProduction.outputSettings.${label}`)}
                      disabled={isReproductionFieldDisabled}
                      state={
                        isReproductionFieldDisabled ? 'inactive' : 'enabled'
                      }
                      sizeVariant="medium"
                      id={`ProductionComponentsFolderDetails_FolderName_${apiField}`}
                      dataTestId={`ReProduction_outputSettings_FolderName_${apiField}`}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box className="delivery-section">
              <Box className="toggle-row">
                <ArrToggle
                  checked={splitSubFolders}
                  onChange={handleToggleChange('splitSubFolders', [
                    'splitCount',
                  ])}
                  disabled={isReproductionFieldDisabled}
                  dataTestId="ProductionComponentsFolderDetails_ToSplitSubFolders"
                />
                <Typography
                  className="toggle-label typo-text body3_size re-production-label-disabled"
                  component="div"
                  data-testid="reproduction_splitSubFolders_label"
                >
                  {t('reProduction.outputSettings.splitSubFolders')}
                </Typography>
              </Box>
              <Box className="field-row">
                <Box className="field-item">
                  <Typography
                    className="typo-text body3_size label re-production-label-disabled"
                    component="div"
                    data-testid="reproduction_splitCount_label"
                  >
                    {splitCountLabel}
                  </Typography>
                  <ArrTextField
                    placeholder={t(
                      'reProduction.outputSettings.splitCountPlaceholder'
                    )}
                    {...register('splitCount')}
                    value={watch('splitCount') || ''}
                    disabled={isReproductionFieldDisabled}
                    state={
                      isReproductionFieldDisabled
                        ? 'inactive'
                        : errors.splitCount
                          ? 'error'
                          : 'enabled'
                    }
                    showErrorIcon={!!errors.splitCount}
                    supportText={errors.splitCount?.message || ''}
                    sizeVariant="medium"
                    id="ProductionComponentsFolderDetails_SplitCount"
                    dataTestId="ReProduction_outputSettings_SplitCount"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <ArrModal
        showModal={showArchiveWarning}
        showClose={false}
        onClose={handleArchiveWarningCancel}
        title={t('reProduction.outputSettings.archiveWarning.title')}
        dataTestId="ReProduction_outputSettings_ArchiveWarning"
        description={t(
          'reProduction.outputSettings.archiveWarning.description'
        )}
        buttons={[
          {
            id: 'archive-warning-cancel',
            label: t('reProduction.outputSettings.archiveWarning.cancel'),
            variant: 'secondary',
            onClick: handleArchiveWarningCancel,
            dataTestId: 'ReProduction_outputSettings_ArchiveWarning_Cancel',
          },
          {
            id: 'archive-warning-confirm',
            label: t('reProduction.outputSettings.archiveWarning.confirm'),
            variant: 'primary',
            onClick: handleArchiveWarningConfirm,
            dataTestId: 'ReProduction_outputSettings_ArchiveWarning_Confirm',
          },
        ]}
      />

      <ArrModal
        showModal={folderModalState.show}
        onClose={handleFolderModalCancel}
        headerTitle={createFolderTitle}
        description={folderModalState.message || createFolderDescription}
        dataTestId="ReProduction_outputSettings_CreateFolderModal"
        buttons={[
          {
            id: 'create-folder-cancel',
            label: createFolderCancel,
            variant: 'secondary',
            onClick: handleFolderModalCancel,
            dataTestId: 'ReProduction_outputSettings_CreateFolder_Cancel',
          },
          {
            id: 'create-folder-confirm',
            label: createFolderConfirm,
            variant: 'primary',
            onClick: handleFolderModalConfirm,
            disabled: createDestinationMutation.isPending,
            dataTestId: 'ReProduction_outputSettings_CreateFolder_Confirm',
          },
        ]}
      />

      <ArrModal
        showModal={showRootPathError}
        showClose={true}
        onClose={() => setShowRootPathError(false)}
        title={t('common.error')}
        description={t('reProduction.outputSettings.errors.rootPathRequired')}
        dataTestId="ReProduction_outputSettings_RootPathError"
        buttons={[
          {
            id: 'root-path-error-ok',
            label: t('common.ok'),
            variant: 'primary',
            onClick: () => setShowRootPathError(false),
            dataTestId: 'ReProduction_outputSettings_RootPathError_OK',
          },
        ]}
      />
    </>
  );
};

const OutputSettings = forwardRef(OutputSettingsInner);

OutputSettings.displayName = 'OutputSettings';

export default OutputSettings;
